import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import logo from "@/assets/logo.png";
import {
  Settings,
  Users as UsersIcon,
  LayoutDashboard,
  LogOut,
  ChevronDown,
  Building2,
  Users2,
  HardDrive,
} from "lucide-react";

export const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(
    location.pathname.startsWith("/pengaturan")
  );

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navBase =
    "flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm font-medium transition-colors duration-200";

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {/* Sidebar */}
      <aside
        className="flex w-64 flex-col border-r border-border bg-surface"
        data-testid="sidebar"
      >
        <div className="flex h-16 items-center gap-2 border-b border-border px-6 font-heading text-base font-semibold tracking-tight">
          <img src={logo} alt="Pengepul ABG" className="h-8 w-8 object-contain" data-testid="sidebar-logo" />
          Pengepul ABG
        </div>

        <nav className="flex-1 space-y-1 p-4">
          <NavLink
            to="/dashboard"
            data-testid="sidebar-dashboard-link"
            className={({ isActive }) =>
              `${navBase} ${
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              }`
            }
          >
            <LayoutDashboard className="h-4 w-4" />
            Dashboard
          </NavLink>

          <NavLink
            to="/mitra"
            data-testid="sidebar-mitra-link"
            className={({ isActive }) =>
              `${navBase} ${
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              }`
            }
          >
            <Users2 className="h-4 w-4" />
            Mitra
          </NavLink>

          {/* Pengaturan main menu */}
          <button
            type="button"
            data-testid="sidebar-settings-toggle"
            onClick={() => setSettingsOpen((s) => !s)}
            className={`${navBase} w-full justify-between ${
              location.pathname.startsWith("/pengaturan")
                ? "text-foreground"
                : "text-muted-foreground hover:bg-accent hover:text-foreground"
            }`}
          >
            <span className="flex items-center gap-3">
              <Settings className="h-4 w-4" />
              Pengaturan
            </span>
            <ChevronDown
              className={`h-4 w-4 transition-transform duration-200 ${
                settingsOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {settingsOpen && (
            <div className="ml-4 border-l border-border pl-3" data-testid="sidebar-settings-submenu">
              <NavLink
                to="/pengaturan/user"
                data-testid="sidebar-user-link"
                className={({ isActive }) =>
                  `${navBase} ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`
                }
              >
                <UsersIcon className="h-4 w-4" />
                User
              </NavLink>
              <NavLink
                to="/pengaturan/cabang"
                data-testid="sidebar-cabang-link"
                className={({ isActive }) =>
                  `${navBase} ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`
                }
              >
                <Building2 className="h-4 w-4" />
                Cabang
              </NavLink>
              <NavLink
                to="/pengaturan/s3"
                data-testid="sidebar-s3-link"
                className={({ isActive }) =>
                  `${navBase} ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`
                }
              >
                <HardDrive className="h-4 w-4" />
                Penyimpanan S3
              </NavLink>
            </div>
          )}
        </nav>

        <div className="border-t border-border p-4">
          <div className="mb-3 px-1">
            <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Masuk sebagai</p>
            <p className="mt-1 truncate font-medium" data-testid="sidebar-current-user">
              {user?.username}
            </p>
          </div>
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Keluar
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
};
